"use client";

import axios, { isAxiosError } from "axios";
import { CheckCircle, Home, Package, Truck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { logEvent } from "@/ui/analytics/google";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;

const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;

interface OrderData {
	order_id: string;
	order_status: string;
	shipping_address: string;
	date_cust_ordered: string;
	date_created: string;
}

export function Tracking() {
	const [orderID, setOrderID] = useState("");
	const [orderData, setOrderData] = useState<OrderData | null>(null);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError(null);
		try {
			// Log click event to GA
			logEvent("Click", "Tracking Lookup", orderID);

			const response = await axios.get<OrderData>(`${API_URL}/tracking/${orderID}`, {
				withCredentials: true,
				headers: {
					"x-api-key": API_KEY,
				},
			});
			if (response.status === 404) {
				throw new Error("Not found.");
			}

			setOrderData(response.data);
		} catch (err) {
			if (isAxiosError(err)) {
				if (err.response && err.response.status === 404) {
					setError("Order was not found. Please try again or contact us for help.");
				} else {
					setError("Error fetching order data. Please try again later.");
				}
			} else {
				setError("An unexpected error occurred.");
			}
		}
	};

	const stages = [
		{ status: "Order Placed", icon: <Package /> },
		{ status: "Processing", icon: <CheckCircle /> },
		{ status: "Shipped", icon: <Truck /> },
		{ status: "Delivered", icon: <Home /> },
	];

	const getCurrentStage = (status: string) => {
		switch (status) {
			case "Order Placed":
				return 0;
			case "Processing":
				return 1;
			case "Shipped":
				return 2;
			case "Delivered":
				return 3;
			default:
				return 0;
		}
	};

	const current = orderData ? getCurrentStage(orderData.order_status) : -1;

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<header className="mx-auto max-w-prose text-center">
				<p className="label-caps mb-4 text-ink-muted">Order lookup</p>
				<h1 className="font-serif text-display font-light text-mg-black">Track your order</h1>
			</header>

			{!orderData ? (
				<form onSubmit={handleSubmit} className="mx-auto mt-12 max-w-md">
					<label htmlFor="orderID" className="label-caps mb-3 block text-ink-muted">
						Order ID
					</label>
					<div className="flex border-b border-mg-black/20 focus-within:border-mg-gold-deep">
						<input
							id="orderID"
							type="text"
							placeholder="e.g. 775d0fc3-922a-…"
							value={orderID}
							onChange={(e) => setOrderID(e.target.value)}
							className="w-full border-0 bg-transparent px-0 py-2 text-mg-black placeholder:text-ink-faint focus:ring-0"
							required
						/>
					</div>
					{error ? <p className="mt-4 text-sm text-ink-muted">{error}</p> : null}
					<button
						type="submit"
						className="label-caps mt-8 w-full bg-mg-black px-7 py-4 text-bone transition-colors duration-300 ease-gallery hover:bg-mg-gold hover:text-mg-black"
					>
						Track Order
					</button>
				</form>
			) : (
				<div className="mx-auto mt-16 max-w-3xl">
					{/* Status timeline — a quiet gold progression. */}
					<ol className="flex items-start justify-between">
						{stages.map((stage, index) => {
							const done = index <= current;
							return (
								<li key={stage.status} className="relative flex flex-1 flex-col items-center text-center">
									{index < stages.length - 1 ? (
										<span
											className={`absolute left-1/2 top-4 h-px w-full ${
												index < current ? "bg-mg-gold-deep" : "bg-mg-black/15"
											}`}
										/>
									) : null}
									<span
										className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border ${
											done
												? "border-mg-gold-deep bg-bone text-mg-gold-deep"
												: "border-mg-black/15 bg-bone text-ink-faint"
										}`}
									>
										<span className="[&>svg]:h-4 [&>svg]:w-4">{stage.icon}</span>
									</span>
									<span className={`label-caps mt-3 ${done ? "text-mg-black" : "text-ink-faint"}`}>
										{stage.status}
									</span>
								</li>
							);
						})}
					</ol>

					<dl className="mt-16 space-y-4 border-t border-mg-black/10 pt-8">
						<TrackRow label="Order ID">{orderData.order_id}</TrackRow>
						<TrackRow label="Date ordered">{orderData.date_created || orderData.date_cust_ordered}</TrackRow>
						<TrackRow label="Status">{orderData.order_status}</TrackRow>
					</dl>

					<button onClick={() => setOrderData(null)} className="link-gallery mt-10 text-sm">
						Look up another order
					</button>
				</div>
			)}
		</section>
	);
}

function TrackRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex gap-6">
			<dt className="label-caps w-32 shrink-0 pt-0.5 text-ink-faint">{label}</dt>
			<dd className="text-sm text-mg-black">{children}</dd>
		</div>
	);
}
